import { Button, InputNumber, Spin } from 'antd'
import React from 'react'
import ProductImage from '../../../../components/shared/ProductImage'
import { CHECKOUT_LINE_GRID_COLUMNS } from 'src/utils/checkoutLineGrid'
import styled from 'styled-components/macro'

const CheckoutItemRow = ({
  item,
  isDuplicate,
  onQuantityChange,
  onRemove,
  isPricesHidden,
  isLoading,
  errorMessage,
  onRetry,
}) => {
  const handleQuantityChange = value => {
    if (value === null || value === undefined || value < 0) {
      return
    }
    if (value === 0) {
      onRemove(item.orderId)
    } else {
      onQuantityChange(item.orderId, value)
    }
  }

  return (
    <ItemRow $isDuplicate={isDuplicate} $isLoading={isLoading}>
      <ImageCell>
        {(() => {
          const imageValue = item.imageId || item.cloudinaryImageId
          // Full URLs (e.g. venue-uploaded photos) can't go through the
          // Cloudinary component, which expects a Cloudinary public ID.
          if (
            imageValue &&
            (imageValue.startsWith('https://') ||
              imageValue.startsWith('http://'))
          ) {
            return (
              <img
                src={imageValue}
                width={60}
                height={60}
                style={{ borderRadius: 10, objectFit: 'cover' }}
                alt=''
              />
            )
          }
          return <ProductImage imageId={imageValue} />
        })()}
      </ImageCell>
      <TitleCell>
        <ProductTitle>{item.productTitle}</ProductTitle>
        {item.productNotes && <ProductNotes>{item.productNotes}</ProductNotes>}
      </TitleCell>
      <SizeCell>{item.size || '-'}</SizeCell>
      <PriceCell>{isPricesHidden ? '' : `£${item.price.toFixed(2)}`}</PriceCell>
      <SupplierCell>{item.supplierTitle || '-'}</SupplierCell>
      <SkuCell>{item.sku || '-'}</SkuCell>
      <QuantityCell>
        <QuantityWrapper>
          <InputNumber
            style={{ width: 70 }}
            min={0}
            value={item.quantity}
            onChange={handleQuantityChange}
            disabled={isLoading}
          />
          {isLoading && (
            <LoadingSpinner>
              <Spin size='small' />
            </LoadingSpinner>
          )}
          {errorMessage && (
            <ErrorWrapper>
              <ErrorMessage>{errorMessage}</ErrorMessage>
              {onRetry && (
                <RetryButton
                  type='link'
                  size='small'
                  onClick={() => onRetry(item.orderId, 'update', item.quantity)}
                >
                  Retry?
                </RetryButton>
              )}
            </ErrorWrapper>
          )}
        </QuantityWrapper>
      </QuantityCell>
      <TotalCell>{isPricesHidden ? '' : `£${item.total.toFixed(2)}`}</TotalCell>
      <ActionCell>
        <Button
          type='danger'
          size='small'
          onClick={() => onRemove(item.orderId)}
          disabled={isLoading}
          loading={isLoading}
        >
          Remove
        </Button>
      </ActionCell>
    </ItemRow>
  )
}

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: ${CHECKOUT_LINE_GRID_COLUMNS};
  gap: 15px;
  min-width: 52rem;
  align-items: center;
  padding: 19px 15px;
  background-color: ${props =>
    props.$isDuplicate ? '#fff1f0' : 'transparent'};
  border: ${props => (props.$isDuplicate ? '2px solid #ff4d4f' : 'none')};
  border-bottom: ${props =>
    props.$isDuplicate ? '2px solid #ff4d4f' : '1px solid #e8e8e8'};
  opacity: ${props => (props.$isLoading ? 0.6 : 1)};
  transition: opacity 0.2s ease;

  &.duplicate-row {
    border: 2px solid #ff4d4f;
    background: #fff1f0;
  }

  &:hover {
    background-color: ${props => (props.$isDuplicate ? '#ffe7e6' : '#fafafa')};
  }
`

const ImageCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const TitleCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`

const ProductTitle = styled.div`
  font-weight: 500;
  color: #333;
  overflow-wrap: anywhere;
`

const ProductNotes = styled.div`
  font-size: 12px;
  color: #999;
  font-style: italic;
`

const SizeCell = styled.div`
  color: #666;
`

const PriceCell = styled.div`
  font-weight: 500;
  color: #333;
`

const SupplierCell = styled.div`
  color: #666;
`

const SkuCell = styled.div`
  color: #999;
  font-size: 12px;
  white-space: nowrap;
`

const QuantityCell = styled.div`
  display: flex;
  align-items: center;
`

const QuantityWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
`

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
`

const ErrorMessage = styled.div`
  font-size: 11px;
  color: #ff4d4f;
  line-height: 1.2;
`

const RetryButton = styled(Button)`
  padding: 0;
  height: auto;
  font-size: 11px;
  line-height: 1.2;
`

const TotalCell = styled.div`
  font-weight: 600;
  color: #333;
`

const ActionCell = styled.div`
  display: flex;
  align-items: center;
`

export default CheckoutItemRow
